import { supabase } from './client';
import { getCurrentEmpresaId } from './auth-utils';

export interface PaymentInstallmentConfig {
  id: string;
  empresa_id: string;
  payment_method: string;
  max_installments: number;
  days_between_installments: number;
  created_at: string;
  updated_at: string;
}

/**
 * Lista todas as configurações de parcelamento da empresa
 */
export async function listarConfiguracoes(): Promise<PaymentInstallmentConfig[]> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return [];
    }

    const { data, error } = await supabase
      .from('payment_installment_configs')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('payment_method', { ascending: true });

    if (error) {
      console.error('Erro ao listar configurações de parcelamento:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Erro ao listar configurações de parcelamento:', error);
    return [];
  }
}

/**
 * Busca configuração específica de uma forma de pagamento
 */
export async function buscarConfiguracao(paymentMethod: string): Promise<PaymentInstallmentConfig | null> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return null;
    }

    const { data, error } = await supabase
      .from('payment_installment_configs')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('payment_method', paymentMethod)
      .single();

    if (error) {
      console.error(`Erro ao buscar configuração para ${paymentMethod}:`, error);
      return null;
    }

    return data || null;
  } catch (error) {
    console.error(`Erro ao buscar configuração para ${paymentMethod}:`, error);
    return null;
  }
}

/**
 * Cria ou atualiza configuração de parcelamento
 */
export async function salvarConfiguracao(
  paymentMethod: string,
  maxInstallments: number,
  daysBetween: number
): Promise<{ ok: boolean; data?: PaymentInstallmentConfig; error?: string }> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return { ok: false, error: 'Usuário não tem empresa associada' };
    }

    // Tentar atualizar primeiro
    const { data: existingData } = await supabase
      .from('payment_installment_configs')
      .select('id')
      .eq('empresa_id', empresaId)
      .eq('payment_method', paymentMethod)
      .single();

    if (existingData) {
      // Atualizar
      const { data, error } = await supabase
        .from('payment_installment_configs')
        .update({
          max_installments: maxInstallments,
          days_between_installments: daysBetween,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existingData.id)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar configuração:', error);
        return { ok: false, error: error.message };
      }

      return { ok: true, data: data as PaymentInstallmentConfig };
    } else {
      // Inserir
      const { data, error } = await supabase
        .from('payment_installment_configs')
        .insert({
          empresa_id: empresaId,
          payment_method: paymentMethod,
          max_installments: maxInstallments,
          days_between_installments: daysBetween,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar configuração:', error);
        return { ok: false, error: error.message };
      }

      return { ok: true, data: data as PaymentInstallmentConfig };
    }
  } catch (error: any) {
    console.error('Erro ao salvar configuração:', error);
    return { ok: false, error: error.message || 'Erro ao salvar configuração' };
  }
}

/**
 * Deleta configuração de parcelamento
 */
export async function deletarConfiguracao(configId: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return { ok: false, error: 'Usuário não tem empresa associada' };
    }

    const { error } = await supabase
      .from('payment_installment_configs')
      .delete()
      .eq('id', configId)
      .eq('empresa_id', empresaId);

    if (error) {
      console.error('Erro ao deletar configuração:', error);
      return { ok: false, error: error.message };
    }

    return { ok: true };
  } catch (error: any) {
    console.error('Erro ao deletar configuração:', error);
    return { ok: false, error: error.message || 'Erro ao deletar configuração' };
  }
}

/**
 * Gera datas de vencimento baseado em configuração de parcelamento
 * Retorna array com as datas de cada parcela
 */
export function gerarDatasParcelamento(
  startDate: string,
  installmentCount: number,
  daysBetweenInstallments: number
): string[] {
  const datas: string[] = [];
  const baseDate = new Date(startDate);

  for (let i = 0; i < installmentCount; i++) {
    const parcelDate = new Date(baseDate);
    parcelDate.setDate(parcelDate.getDate() + i * daysBetweenInstallments);
    datas.push(parcelDate.toISOString().split('T')[0]); // Formato YYYY-MM-DD
  }

  return datas;
}

/**
 * Calcula valor de cada parcela
 */
export function calcularValorParcela(totalValue: number, installmentCount: number): number {
  return Math.round((totalValue / installmentCount) * 100) / 100;
}
